// src/pages/Fleet.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../styles/fleet.css";

import saloonImg from "../assets/saloon1.webp";
import sevenSeaterImg from "../assets/7seater.webp";
import nineSeaterImg from "../assets/9seater.webp";
import fourteenSeaterImg from "../assets/14seater.webp";
import twentyFiveSeaterImg from "../assets/25seater.webp";

const Fleet = () => {
  const fleetSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "CheapCabGrab Airport Taxi & Minibus Fleet",
    provider: {
      "@type": "Organization",
      name: "CheapCabGrab",
      url: "https://www.cheapcabgrab.com",
    },
    areaServed: [
      "Heathrow Airport",
      "London",
      "Slough",
      "Windsor",
      "Maidenhead",
      "Reading",
    ],
    serviceType: "Airport taxi, minibus and coach transfers",
  };

  return (
    <div className="fleet-page">
      <Helmet>
        <title>
          CheapCabGrab Fleet – Saloon, MPV, Minibus & Coach Hire for Heathrow & London
        </title>
        <meta
          name="description"
          content="Explore the CheapCabGrab fleet – saloon cars, MPVs, 7–9 seater minibuses, 14-seater and 25-seater mini coaches for Heathrow, London and UK transfers. Fixed-price fares, 24/7 availability, flight tracking."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/fleet" />
        <script type="application/ld+json">
          {JSON.stringify(fleetSchema)}
        </script>
      </Helmet>

      {/* HERO */}
      <header className="fleet-hero">
        <div className="fleet-hero-inner">
          <h1>🚗 CheapCabGrab Fleet – Vehicles for Every Journey</h1>
          <p>
            From solo travellers to large groups, CheapCabGrab offers comfortable,
            licensed vehicles for <strong>Heathrow, London, Slough, Windsor,
            Maidenhead and beyond</strong>. All with fixed-price fares and
            airport-focused service.
          </p>

          <div className="fleet-hero-ctas">
            <Link to="/" className="fleet-primary-btn">
              🚖 Get a Taxi Quote
            </Link>
            <Link to="/contact" className="fleet-secondary-btn">
              📩 Contact Support
            </Link>
          </div>
        </div>
      </header>

      {/* IN-PAGE NAV */}
      <nav className="fleet-subnav">
        <a href="#overview">Overview</a>
        <a href="#cars">Cars & MPVs</a>
        <a href="#minibuses">Minibuses & Coaches</a>
        <a href="#included">What&apos;s Included</a>
        <a href="#heathrow">Heathrow Transfers</a>
        <a href="#drivers">Our Drivers</a>
        <a href="#faqs">Fleet FAQs</a>
      </nav>

      <main className="fleet-main">
        {/* OVERVIEW */}
        <section id="overview" className="fleet-section">
          <h2>🌍 One fleet, many options</h2>
          <div className="fleet-two-col">
            <div className="fleet-text">
              <p>
                CheapCabGrab is built around simple, reliable{" "}
                <strong>fixed-price airport transfers</strong>. Our fleet ranges
                from comfortable saloon cars up to <strong>25-seater mini
                coaches</strong>, so you can choose exactly what you need for your
                journey.
              </p>
              <p>
                All vehicles are <strong>fully licensed</strong>, maintained to
                high standards and driven by experienced, DBS-checked drivers.
                For larger minibuses and mini coaches we work with a trusted PSV
                partner to ensure full compliance and safety.
              </p>
            </div>
            <div className="fleet-highlight-card">
              <h3>Quick summary</h3>
              <ul>
                <li>✅ Saloon cars for 1–3 passengers</li>
                <li>✅ MPVs & 7-seaters for families & luggage</li>
                <li>✅ 9–14 seater minibuses for groups</li>
                <li>✅ 25-seater mini coaches for events & tours</li>
                <li>✅ 24/7 Heathrow & London coverage</li>
              </ul>
              <Link to="/services" className="fleet-inline-link">
                View all CheapCabGrab services →
              </Link>
            </div>
          </div>
        </section>

        {/* CARS & MPVs */}
        <section id="cars" className="fleet-section">
          <h2>🚘 Cars & MPVs</h2>
          <p className="fleet-section-intro">
            Ideal for everyday trips, business travel and family airport runs.
            Fares below are <strong>guide ranges</strong> for Heathrow → Central
            London – always check the live quote for your exact price.
          </p>

          <div className="fleet-card-grid">
            {/* Saloon */}
            <article className="fleet-card">
              <img
                src={saloonImg}
                alt="Standard saloon taxi – CheapCabGrab"
                className="fleet-vehicle-image"
              />
              <h3>Saloon Car</h3>
              <p>
                Comfortable everyday car for most journeys – perfect for solo
                travellers, couples or light luggage.
              </p>
              <ul className="fleet-spec-list">
                <li>👥 Seats: <strong>1–3 passengers</strong></li>
                <li>🧳 Luggage: <strong>2 medium or 1 large suitcase</strong></li>
                <li>💷 Guide fare Heathrow → Central London: <strong>£60–£80</strong></li>
                <li>⏰ Availability: <strong>24/7</strong></li>
              </ul>
              <p className="fleet-usecase">
                Best for: business trips, hotel runs, station pick-ups and solo
                airport transfers.
              </p>
              <p className="fleet-note">
                Flight tracking included. Meet &amp; Greet available as an optional
                extra. Child seats can be requested when booking.
              </p>
            </article>

            {/* 7 Seater MPV */}
            <article className="fleet-card">
              <img
                src={sevenSeaterImg}
                alt="7-seater MPV taxi – CheapCabGrab"
                className="fleet-vehicle-image"
              />
              <h3>7-Seater MPV</h3>
              <p>
                Extra space for families, friends and luggage. Great choice for
                Heathrow hotels and longer journeys.
              </p>
              <ul className="fleet-spec-list">
                <li>👥 Seats: <strong>up to 5 passengers</strong> comfortably</li>
                <li>🧳 Luggage: <strong>3–4 medium suitcases</strong></li>
                <li>💷 Guide fare Heathrow → Central London: <strong>£75–£100</strong></li>
                <li>⏰ Availability: <strong>24/7 (pre-book recommended)</strong></li>
              </ul>
              <p className="fleet-usecase">
                Best for: families, extra luggage, pushchairs, longer stays or
                groups with golf bags/sports kits.
              </p>
              <p className="fleet-note">
                Flight tracking included. Meet &amp; Greet optional. Please list
                child seats and any special luggage in the booking notes.
              </p>
            </article>
          </div>
        </section>

        {/* MINIBUSES & COACHES */}
        <section id="minibuses" className="fleet-section">
          <h2>🚌 Minibuses & Mini Coaches</h2>
          <p className="fleet-section-intro">
            Perfect for group transfers, events, tours and conference travel.
            Minibuses and mini coaches are operated in partnership with a{" "}
            <strong>fully licensed PSV operator</strong>.
          </p>

          <div className="fleet-card-grid">
            {/* 9 Seater */}
            <article className="fleet-card">
              <img
                src={nineSeaterImg}
                alt="9-seater minibus – CheapCabGrab"
                className="fleet-vehicle-image"
              />
              <h3>9-Seater Minibus</h3>
              <p>
                Compact minibus for small groups who want to travel together
                with luggage in one vehicle.
              </p>
              <ul className="fleet-spec-list">
                <li>👥 Seats: <strong>up to 8 passengers</strong></li>
                <li>🧳 Luggage: <strong>plenty of space for cases & bags</strong></li>
                <li>💷 Guide fare Heathrow → Central London: <strong>£110–£140</strong></li>
                <li>⏰ Availability: <strong>pre-book only</strong></li>
              </ul>
              <p className="fleet-usecase">
                Best for: family groups, wedding parties, small teams and cruise
                transfers.
              </p>
              <p className="fleet-note">
                Operated with a licensed PSV partner. Meet &amp; Greet and multiple
                pick-ups can be arranged on request.
              </p>
            </article>

            {/* 14 Seater */}
            <article className="fleet-card">
              <img
                src={fourteenSeaterImg}
                alt="14-seater minibus – CheapCabGrab"
                className="fleet-vehicle-image"
              />
              <h3>14-Seater Minibus</h3>
              <p>
                Larger minibus for bigger groups heading to Heathrow, London or
                regional destinations.
              </p>
              <ul className="fleet-spec-list">
                <li>👥 Seats: <strong>up to 14 passengers</strong></li>
                <li>🧳 Luggage: <strong>large dedicated luggage space</strong></li>
                <li>💷 Guide fare Heathrow → Central London: <strong>£160–£200</strong></li>
                <li>⏰ Availability: <strong>pre-book only</strong></li>
              </ul>
              <p className="fleet-usecase">
                Best for: sports teams, corporate groups, school trips,
                conference transfers.
              </p>
              <p className="fleet-note">
                Operated in partnership with{" "}
                <strong>RHP Minibus Hire UK Limited</strong> (PSV). Ask about
                tailored itineraries for events and tours.
              </p>
            </article>

            {/* 25 Seater */}
            <article className="fleet-card">
              <img
                src={twentyFiveSeaterImg}
                alt="25-seater mini coach – CheapCabGrab"
                className="fleet-vehicle-image"
              />
              <h3>25-Seater Mini Coach</h3>
              <p>
                Mini coach for larger groups who want a single, comfortable
                vehicle for their journey.
              </p>
              <ul className="fleet-spec-list">
                <li>👥 Seats: <strong>up to 25 passengers</strong></li>
                <li>🧳 Luggage: <strong>space for group luggage & equipment</strong></li>
                <li>💷 Guide fare Heathrow → Central London: <strong>£230–£280</strong></li>
                <li>⏰ Availability: <strong>pre-book only</strong></li>
              </ul>
              <p className="fleet-usecase">
                Best for: corporate events, large family groups, tour groups,
                weddings and special occasions.
              </p>
              <p className="fleet-note">
                Operated by a fully licensed PSV partner. Multi-stop itineraries,
                event timing and on-board requirements can be discussed with our
                team.
              </p>
            </article>
          </div>

          <p className="fleet-disclaimer">
            All fares above are <strong>guide ranges only</strong> for typical
            Heathrow → Central London journeys. For an exact price tailored to
            your route, date and time, please use our live quote tool or contact
            support.
          </p>
        </section>

        {/* WHAT'S INCLUDED */}
        <section id="included" className="fleet-section">
          <h2>✅ What&apos;s included with CheapCabGrab</h2>
          <div className="fleet-card-grid small-grid">
            <div className="fleet-card">
              <h3>Fixed-Price Fares</h3>
              <p>
                The price you see is the price you pay – no surge pricing or
                unexpected extras from CheapCabGrab.
              </p>
            </div>
            <div className="fleet-card">
              <h3>24/7 Availability</h3>
              <p>
                Early-morning flights, late-night arrivals, weekends and
                holidays – our fleet operates around the clock.
              </p>
            </div>
            <div className="fleet-card">
              <h3>Flight Tracking</h3>
              <p>
                For most airlines we track your flight, so if you&apos;re early or
                delayed we adjust the pick-up time accordingly.
              </p>
            </div>
            <div className="fleet-card">
              <h3>Meet &amp; Greet (Optional)</h3>
              <p>
                Not included by default, but can be requested as a paid extra –
                ideal for business guests and family arrivals.
              </p>
            </div>
            <div className="fleet-card">
              <h3>Child Seats on Request</h3>
              <p>
                Tell us in advance if you need child or booster seats, and we
                will allocate a suitable vehicle where possible.
              </p>
            </div>
            <div className="fleet-card">
              <h3>Accessible Options</h3>
              <p>
                We aim to support passengers with mobility needs – please share
                details when booking so we can plan the right vehicle and
                pick-up point.
              </p>
            </div>
          </div>
        </section>

        {/* HEATHROW FOCUS */}
        <section id="heathrow" className="fleet-section">
          <h2>✈️ Perfect for Heathrow transfers</h2>
          <div className="fleet-two-col">
            <div className="fleet-text">
              <p>
                Every CheapCabGrab vehicle type is available for{" "}
                <strong>Heathrow Airport transfers</strong> – from saloon cars
                for individuals to minibuses and mini coaches for large groups.
              </p>
              <p>
                We cover all terminals (T2, T3, T4, T5) with clear meeting
                points and guidance in your confirmation email.
              </p>
              <ul className="fleet-bullet">
                <li>🚖 Direct transfers to hotels, homes and offices</li>
                <li>🚐 Minibuses and coaches for groups & tours</li>
                <li>ℹ️ Guidance on Heathrow drop-off charges and pick-up rules</li>
              </ul>
            </div>
            <div className="fleet-text">
              <p>
                For more Heathrow-specific information, see our dedicated pages:
              </p>
              <ul className="fleet-link-list">
                <li>
                  <Link to="/heathrow" className="fleet-inline-link">
                    Heathrow Airport information →
                  </Link>
                </li>
                <li>
                  <Link to="/heathrow-taxi" className="fleet-inline-link">
                    Heathrow taxi fares & booking →
                  </Link>
                </li>
                <li>
                  <Link to="/terminalfinder" className="fleet-inline-link">
                    Terminal Finder →
                  </Link>
                </li>
                <li>
                  <Link to="/airlineContacts" className="fleet-inline-link">
                    Airline contact details →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* DRIVERS */}
        <section id="drivers" className="fleet-section">
          <h2>👮 Our drivers</h2>
          <div className="fleet-two-col">
            <div className="fleet-text">
              <p>
                Behind every vehicle is an experienced, professional driver.
                CheapCabGrab only works with{" "}
                <strong>licensed, DBS-checked drivers</strong> who understand
                airport timings, local routes and customer care.
              </p>
              <ul className="fleet-bullet">
                <li>✅ Licensed under local council regulations</li>
                <li>✅ DBS checks, with enhanced checks for PSV operations</li>
                <li>✅ Airport-specialist experience</li>
                <li>✅ Awareness of mobility & accessibility needs</li>
              </ul>
            </div>
            <div className="fleet-text">
              <p>
                For larger vehicles (minibuses and mini coaches), services are
                operated in partnership with{" "}
                <strong>RHP Minibus Hire UK Limited</strong>, a{" "}
                <strong>fully licensed PSV operator</strong>.
              </p>
              <p>
                To learn more, visit our dedicated{" "}
                <Link to="/our-drivers" className="fleet-inline-link">
                  Our Drivers
                </Link>{" "}
                page or, if you are a licensed driver interested in working with
                us, see our{" "}
                <Link to="/driver" className="fleet-inline-link">
                  driver onboarding
                </Link>{" "}
                section.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ MINI SECTION */}
        <section id="faqs" className="fleet-section">
          <h2>❓ Fleet & vehicle FAQs</h2>
          <div className="fleet-faq-grid">
            <div className="fleet-faq-card">
              <h3>Are the fares on this page exact?</h3>
              <p>
                No – they are <strong>guide ranges</strong> for typical Heathrow
                → Central London journeys. Your exact fare depends on your
                pickup, drop-off, date and time. Always check the live quote on
                our homepage.
              </p>
            </div>
            <div className="fleet-faq-card">
              <h3>Is Meet &amp; Greet included?</h3>
              <p>
                Meet &amp; Greet is <strong>not included by default</strong>, but
                can be added as a paid extra. This is useful when collecting
                guests, elderly passengers or unaccompanied young travellers.
              </p>
            </div>
            <div className="fleet-faq-card">
              <h3>Can I request a specific vehicle type?</h3>
              <p>
                Yes – simply choose your preferred vehicle type when requesting
                a quote. We will confirm availability based on fleet and timings.
              </p>
            </div>
            <div className="fleet-faq-card">
              <h3>Do you offer corporate or regular bookings?</h3>
              <p>
                Yes, we can support repeat bookings, corporate accounts and
                regular group transfers. Please{" "}
                <Link to="/contact" className="fleet-inline-link">
                  contact our team
                </Link>{" "}
                to discuss your requirements.
              </p>
            </div>
          </div>
          <Link to="/faq" className="fleet-inline-link">
            See all CheapCabGrab FAQs →
          </Link>
        </section>

        {/* PARTNER FOOTNOTE */}
        <section className="fleet-partner-note">
          <p>
            Minibus and mini coach journeys are operated in partnership with{" "}
            <strong>RHP Minibus Hire UK Limited</strong>, a fully licensed PSV
            operator. All services are subject to availability and applicable
            licensing conditions.
          </p>
        </section>

        {/* CTA BANNER */}
        <section className="fleet-cta-banner">
          <div className="fleet-cta-inner">
            <h2>Ready to book your CheapCabGrab vehicle?</h2>
            <p>
              Get an instant, fixed-price quote for your Heathrow, London or UK
              transfer – from saloon cars to 25-seater mini coaches.
            </p>
            <div className="fleet-cta-buttons">
              <Link to="/" className="fleet-primary-btn">
                🚖 Get a Quote
              </Link>
              <Link to="/contact" className="fleet-secondary-btn">
                📩 Talk to our team
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Fleet;

